import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  map,
  Observable,
  of,
  Subject,
} from "rxjs";
import {
  AppLoadError,
  AppSubmitError,
  IPayment,
  IPaymentResponse,
  IPreloadData,
  IPreloadDataResponse,
  IPromoCodeSubmit,
} from "../shared/models";
import { PaymentService } from "../shared/services/api/payment.service";
import { HttpErrorResponse } from "@angular/common/http";
import { SharedStoreService } from "./shared-store.service";
import { GtmService } from "../shared/services/gtm.service";

@Injectable({
  providedIn: "root",
})
export class PaymentStoreService {
  private _loading = new BehaviorSubject<boolean>(false);
  private _error = new BehaviorSubject<AppLoadError>(null);
  private _paid = new BehaviorSubject<boolean>(false);
  private _promoValid = new BehaviorSubject<boolean>(false);
  private _promoError = new BehaviorSubject<AppLoadError | null>(null);
  private _initializingPayment = new BehaviorSubject<boolean>(false);
  private _transactionTicket = new BehaviorSubject<string | null>(null);
  private _promoFormError = new BehaviorSubject<AppLoadError | null>(null);
  private _checkingPromoCode = new BehaviorSubject<boolean>(false);
  private _paymentResponse = new BehaviorSubject<IPaymentResponse | null>(null);
  private _payments = new BehaviorSubject<IPayment>(null);

  destroyed$ = new Subject<void>();

  // Observables for external components
  loading$ = this._loading.asObservable();
  error$ = this._error.asObservable();
  paid$ = this._paid.asObservable();
  promoValid$ = this._promoValid.asObservable();
  promoError$ = this._promoError.asObservable();
  initializingPayment$ = this._initializingPayment.asObservable();
  transactionTicket$ = this._transactionTicket.asObservable();
  promoFormError$ = this._promoFormError.asObservable();
  checkingPromoCode$ = this._checkingPromoCode.asObservable();
  paymentResponse$ = this._paymentResponse.asObservable();
  payments$ = this._payments.asObservable();

  constructor(
    private service: PaymentService,
    private sharedStore: SharedStoreService,
    private gtmService: GtmService
  ) {}

  // Getters
  get loading(): boolean {
    return this._loading.getValue();
  }

  get error(): AppLoadError {
    return this._error.getValue();
  }

  get paid(): boolean {
    return this._paid.getValue();
  }

  get promoValid(): boolean {
    return this._promoValid.getValue();
  }

  get promoError(): AppLoadError | null {
    return this._promoError.getValue();
  }

  get initializingPayment(): boolean {
    return this._initializingPayment.getValue();
  }

  get transactionTicket(): string | null {
    return this._transactionTicket.getValue();
  }

  get promoFormError(): AppLoadError | null {
    return this._promoFormError.getValue();
  }

  get checkingPromoCode(): boolean {
    return this._checkingPromoCode.getValue();
  }

  get paymentResponse(): IPaymentResponse | null {
    return this._paymentResponse.getValue();
  }

  get payments(): IPayment {
    return this._payments.getValue();
  }

  get latest(): Observable<IPayment> {
    return this.payments$.pipe(
      map((payments) => {
        if (payments) {
          const { id, applicationFee } = payments;
          return {
            id,
            applicationFee,
          };
        }
        return null;
      })
    );
  }

  // Setters
  set loading(value: boolean) {
    this._loading.next(value);
  }

  set error(value: AppLoadError) {
    this._error.next(value);
  }

  set paid(value: boolean) {
    this._paid.next(value);
  }

  set promoValid(value: boolean) {
    this._promoValid.next(value);
  }

  set promoError(value: AppLoadError | null) {
    this._promoError.next(value);
  }

  set initializingPayment(value: boolean) {
    this._initializingPayment.next(value);
  }

  set transactionTicket(value: string | null) {
    this._transactionTicket.next(value);
  }

  set promoFormError(value: AppLoadError | null) {
    this._promoFormError.next(value);
  }

  set checkingPromoCode(value: boolean) {
    this._checkingPromoCode.next(value);
  }

  set paymentResponse(value: IPaymentResponse | null) {
    this._paymentResponse.next(value);
  }

  set payments(value: IPayment) {
    this._payments.next(value);
  }

  /**
   * @GET
   */
  getApplicationFee(id: string) {
    this.loading = true;

    return this.service.getApplicationFee().pipe(
      map((applicationFeeData) => {
        const newApplicationFee: IPayment = {
          applicationFee: applicationFeeData.applicationFee,
          id: id,
        };
        this.loading = false;
        this.payments = newApplicationFee;
        return newApplicationFee;
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err instanceof HttpErrorResponse ? err.error : err;
        const displayError = error
          ? new AppLoadError(error.id, error.message)
          : new AppLoadError();
        this.promoError = displayError;
        this.loading = false;
        return of(displayError);
      })
    );
  }

  // Preloads the data for moneris to execute the payment
  initPaymentResp(payload: { studentNum: any; fee: number }) {
    return this.service
      .getPreloadData(payload.studentNum, payload.fee.toFixed(2))
      .pipe(
        map((data) => {
          const response: IPreloadDataResponse = {
            response: {
              success: data.response.success,
              ticket: data.response.ticket,
            },
          };

          this._initializingPayment.next(true);
          this._transactionTicket.next(response.response.ticket);
          return response;
        }),
        catchError((err: HttpErrorResponse) => {
          const error = err instanceof HttpErrorResponse ? err.error : err;
          const displayError = error
            ? new AppLoadError(error.id, error.message)
            : new AppLoadError();

          return of(displayError);
        })
      );
  }

  checkPromoCode(payload: IPromoCodeSubmit) {
    this.checkingPromoCode = true;
    return this.service.checkPromoCode(payload).pipe(
      map((payment) => {
        this.checkingPromoCode = false;
        if (payment.message === "success") {
          this.promoValid = true;
          this.sharedStore.triggerNavigationAfterSubmit("CHECK_PROMO_CODE");
          return payment;
        } else {
          this.promoValid = false;
          this.promoFormError = new AppSubmitError(
            null,
            "Promo code is expired or limit has been reached."
          );

          return this.promoFormError;
        }
      }),
      catchError((err: HttpErrorResponse) => {
        const error = err instanceof HttpErrorResponse ? err.error : err;
        const displayError = error
          ? new AppSubmitError(error.id, error.message)
          : new AppSubmitError();
        this.checkingPromoCode = false;
        this.promoFormError = displayError;
        this.error = displayError;
        return of(displayError);
      })
    );
  }

  paymentSuccess() {
    this.paid = true;

    this.gtmService.pushTag({
      event: "international_application_complete",
      step_number: "9",
      step_name: "payment",
    });

    this.sharedStore.triggerNavigationAfterSubmit("PAYMENT");
  }

  clearError(): void {
    this.error = null;
    this.promoError = null;
    this.promoFormError = null;
  }
}
